import React from "react";
import DOMPurify from "dompurify";
import { Info, AlertTriangle, Lightbulb } from "lucide-react";

// Permitir solo etiquetas inline seguras del contenido estático del
// manual (bold/italic/code/mark/<br>, enlaces con rel seguro). Bloquea
// scripts, iframes, event handlers y URLs javascript:.
const SANITIZE_CONFIG = {
  ALLOWED_TAGS: ["b", "strong", "i", "em", "u", "code", "mark", "br", "span", "a"],
  ALLOWED_ATTR: ["href", "target", "rel", "class"],
  ALLOW_DATA_ATTR: false,
};

function Inline({ text }) {
  const clean = DOMPurify.sanitize(String(text ?? ""), SANITIZE_CONFIG);
  return <span dangerouslySetInnerHTML={{ __html: clean }} />;
}

function CalloutIcon({ kind }) {
  const Map = { info: Info, warn: AlertTriangle, tip: Lightbulb };
  const Cmp = Map[kind] || Info;
  return <Cmp className="w-4 h-4 shrink-0 mt-0.5" />;
}

export function HelpBlock({ block }) {
  switch (block.type) {
    case "heading": {
      const level = block.level || 2;
      const Tag = `h${Math.min(level, 4)}`;
      const cls =
        level === 1
          ? "text-2xl font-black tracking-tight text-primary mt-5 mb-3"
          : level === 2
          ? "text-xl font-bold tracking-tight mt-5 mb-3"
          : "text-base font-bold text-primary mt-4 mb-2";
      return <Tag className={cls}><Inline text={block.text} /></Tag>;
    }
    case "p":
      return <p className="text-sm leading-relaxed mb-3"><Inline text={block.text} /></p>;
    case "ul":
      return (
        <ul className="list-disc pl-6 space-y-1.5 text-sm mb-3">
          {block.items.map((it, i) => (
            <li key={i}><Inline text={it} /></li>
          ))}
        </ul>
      );
    case "ol":
      return (
        <ol className="list-decimal pl-6 space-y-1.5 text-sm mb-3">
          {block.items.map((it, i) => (
            <li key={i}><Inline text={it} /></li>
          ))}
        </ol>
      );
    case "callout": {
      const kind = block.kind || "info";
      const stylesMap = {
        info: "bg-blue-50 border-blue-400 text-blue-900 dark:bg-blue-950/30 dark:text-blue-200",
        warn: "bg-amber-50 border-amber-500 text-amber-900 dark:bg-amber-950/30 dark:text-amber-200",
        tip: "bg-emerald-50 border-emerald-500 text-emerald-900 dark:bg-emerald-950/30 dark:text-emerald-200",
      };
      return (
        <div className={`border-l-4 rounded-r px-4 py-3 my-4 flex gap-2 text-sm ${stylesMap[kind]}`}>
          <CalloutIcon kind={kind} />
          <div><Inline text={block.text} /></div>
        </div>
      );
    }
    case "code":
      return (
        <pre className="bg-muted/60 border border-border rounded p-3 text-xs font-mono overflow-x-auto mb-3">
          <code>{block.text}</code>
        </pre>
      );
    case "image": {
      const src = block.src?.startsWith("http") ? block.src : `${process.env.REACT_APP_BACKEND_URL}/api${block.src}`;
      return (
        <figure className="my-4 border border-border rounded-md overflow-hidden bg-muted/30">
          <img
            src={src}
            alt={block.caption || "Captura"}
            className="w-full h-auto block"
            loading="lazy"
          />
          {block.caption && (
            <figcaption className="text-xs text-muted-foreground px-3 py-2 border-t border-border">
              <Inline text={block.caption} />
            </figcaption>
          )}
        </figure>
      );
    }
    case "table":
      return (
        <div className="border border-border rounded-md overflow-x-auto mb-4">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 overline">
              <tr>
                {block.headers.map((h, i) => (
                  <th key={i} className="text-left p-2 font-bold">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {block.rows.map((r, i) => (
                <tr key={i} className="border-t border-border/60">
                  {r.map((c, j) => (
                    <td key={j} className="p-2 align-top"><Inline text={String(c)} /></td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    default:
      return null;
  }
}

export function HelpBlocks({ blocks }) {
  if (!blocks || blocks.length === 0) {
    return <div className="text-sm text-muted-foreground">Sin contenido.</div>;
  }
  return (
    <>
      {blocks.map((b, i) => <HelpBlock key={i} block={b} />)}
    </>
  );
}

export function blocksToPlain(blocks) {
  return (blocks || [])
    .map((b) => {
      if (b.type === "heading" || b.type === "p" || b.type === "callout" || b.type === "code")
        return b.text;
      if (b.type === "ul" || b.type === "ol") return b.items.join(" ");
      if (b.type === "table")
        return [b.headers.join(" "), ...b.rows.map((r) => r.join(" "))].join(" ");
      return "";
    })
    .join(" ")
    .toLowerCase();
}
